import { GetServerSidePropsContext } from 'next/types';
import { simpleSignOut } from '@/hooks/useSignout';
import { MyErrorResponse } from '@/utils/handlers';
import axios, { AxiosError } from 'axios';
import { parseCookies, setCookie } from 'nookies';

let isRefreshing = false;
let failedRequestQueue: {
  resolve: (token: string) => void;
  reject: (error: AxiosError) => void;
}[] = [];

export function setupAPIClient(ctx?: GetServerSidePropsContext) {
  let cookies = parseCookies(ctx);

  const api = axios.create({
    baseURL: process.env.NEXT_PUBLIC_BACKEND_URL ?? 'https://api.obraplay.com',
  });

  if (cookies['obraplay.token']) {
    api.defaults.headers.common[
      'Authorization'
    ] = `Bearer ${cookies['obraplay.token']}`;
  }

  api.interceptors.request.use(
    config => {
      cookies = parseCookies(ctx);

      if (!cookies['obraplay.token']) {
        if (config.headers) {
          config.headers['Authorization'] = '';
        }
      }
      return config;
    },
    error => {
      return Promise.reject(error);
    }
  );

  api.interceptors.response.use(
    response => {
      return response;
    },
    (error: AxiosError<MyErrorResponse>) => {
      if (error.response?.status === 403 || error.response?.status === 400) {
        if (error.response.data?.code === 'token_not_valid') {
          // refresh token
          cookies = parseCookies(ctx);
          const { 'obraplay.refresh': refresh } = cookies;
          const originalConfig = error.config;

          if (!isRefreshing) {
            isRefreshing = true;

            api
              .post('/auth/jwt/refresh/', {
                refresh,
              })
              .then(response => {
                setCookie(ctx, 'obraplay.token', response.data['access'], {
                  maxAge: 60 * 60 * 24, // 1 day
                  path: '/',
                });
                setCookie(ctx, 'obraplay.refresh', response.data['refresh'], {
                  maxAge: 60 * 60 * 24, // 1 day
                  path: '/',
                });
                api.defaults.headers.common[
                  'Authorization'
                ] = `Bearer ${response.data['access']}`;

                failedRequestQueue.forEach(request =>
                  request.resolve(response.data['access'])
                );
                failedRequestQueue = [];
              })
              .catch(error => {
                failedRequestQueue.forEach(request => {
                  request.reject(error);
                });
                failedRequestQueue = [];
                if (typeof window === 'undefined') {
                  simpleSignOut();
                }
              })
              .finally(() => {
                isRefreshing = false;
              });
          }

          return new Promise((resolve, reject) => {
            failedRequestQueue.push({
              resolve: (token: string) => {
                if (originalConfig?.headers) {
                  originalConfig.headers['Authorization'] = `Bearer ${token}`;
                }
                originalConfig && resolve(api(originalConfig));
              },
              reject: (error: AxiosError) => {
                reject(error);
              },
            });
          });
        } else {
          // logout user
          // signOut()
        }
      }

      return Promise.reject(error);
    }
  );

  return api;
}
